import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { PartImageUrl } from "@helpers/partImage";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { LogExport } from "@interfaces/logExport.interface";
import { ExportLogService } from "@services/exportLog.service";
import { ProductionToolService } from "@services/productionTool.service";
import { ProtectedGridService } from "@services/protectedGrid.service";
import { ToastService } from "angular-toastify";
import { ToastApiError } from "@helpers/apiError";

@Component({
	moduleId: module.id,
	selector: "productionTools",
	styleUrls: ["productionTools.css"],
	templateUrl: "productionTools.html"
})
export class ProductionToolsAdminComponent {
	canExportGrid = false;
	deleteEnabled = false;
	editEnabled = true;
	gridLoading = false;
	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerSecondaryButtons: AdminHeaderButton[] = [];
	itemType = "Tool"
	itemUrl: string = location.href.split("/")[3] + "/" + location.href.split("/")[4];
	items: any[] = [];
	title: string;
	
	constructor(private router: Router, private toastService: ToastService, private exportLogService: ExportLogService, private itemService: ProductionToolService, private protectedGridService: ProtectedGridService) {
		this.headerPrimaryButtons.push({ icon: "fal fa-plus", method: "addNewItem", text: `New ${this.itemType}` });
		this.headerPrimaryButtons.push({ icon: "fal fa-calendar-alt", method: "goToPlanning", text: "Planning" });
		this.title = `${this.itemType}s`;
		this.viewRow = this.viewRow.bind(this);
		this.editRow = this.editRow.bind(this);
		this.deleteRow = this.deleteRow.bind(this);
		this.getItems();
		this.checkGridExportPermissions();
	}

	addNewItem() {
		this.router.navigate([`${this.itemUrl}/create`]);
	}
	
	checkGridExportPermissions() {
		this.protectedGridService.canExport("Production Tools")
			.subscribe(
				(res: any) => {
					this.canExportGrid = res.response;
				},
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err);
				},
			);
	}

	deleteRow(e: any) {
		const result = confirm("Are you sure you want to delete this item?");
		if(result)
		{
			this.itemService.deleteSingleById(e.row.data.id)
				.subscribe(
					() => {
						this.toastService.success(this.itemType + " deleted!");
						this.getItems();
					},
					(err) => {
						console.log(err);
						ToastApiError(this.toastService, err);
					},
				);
		}
	}

	editRow(e: any) {
		this.router.navigate([`${this.itemUrl}/${e.row.data.id}_edit`]);
	}

	// The picture of the first part a tool is linked to, so the Image column lines up with the part number
	// column beside it. Through the same helper as the parts grid, which falls back to a placeholder.
	// The tool's parts come down with the grid data already, so there is nothing extra to load.
	firstPartImageUrl(rowData: any): string {
		const parts = (rowData && rowData.productionToolParts) || [];
		const sorted = [...parts].sort((a: any, b: any) => a.id - b.id);
		return PartImageUrl(sorted.length > 0 && sorted[0].part ? sorted[0].part.tileImageUrl : null);
	}

	// The part number of the first part a tool is linked to (earliest link), for the grid column.
	firstPartNumber(rowData: any): string {
		const parts = (rowData && rowData.productionToolParts) || [];
		if (parts.length === 0) return "";
		const sorted = [...parts].sort((a: any, b: any) => a.id - b.id);
		return (sorted[0].part && sorted[0].part.partNumber) || "";
	}

	// The op number of the first part a tool is linked to - the same link the Part and Image columns read, so all
	// three columns describe the same part. Left as the raw stage so the column sorts and filters as a number;
	// stageToOP turns it into OP10 for display.
	firstPartRouterStage(rowData: any): number | null {
		const parts = (rowData && rowData.productionToolParts) || [];
		if (parts.length === 0) return null;
		const sorted = [...parts].sort((a: any, b: any) => a.id - b.id);
		return sorted[0].routerStage || null;
	}

	getItems() {
		this.gridLoading = true;
		this.itemService.getDataGrid()
			.subscribe(
				(res: any) => {
					this.items = res.response;
					this.gridLoading = false;
				},
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err);
				},
			);
	}

	goToPlanning() {
		this.router.navigate(["/admin/toolOrderPlanning"]);
	}

	headerButtonClick(method: any) {
		// @ts-ignore // Required to be able to call the method directly from the variable
		if (this[method]) this[method]();
	}

	logExport() {
		const logExport = new LogExport();
		logExport.gridName = "Production Tools";
		this.exportLogService.logExport(logExport)
			.subscribe(
				() => console.log("Export Logged"),
				(err) => {
					console.log(err);
					ToastApiError(this.toastService, err);
					return false;
				},
			);
	}

	// Router stages are stored as 1, 2, 3 but read as OP10, OP20, OP30, the same wording as the tools grid on the
	// part screen. Blank rather than "None" when no part is linked, to match the empty Part column beside it.
	stageToOP(cellInfo: any) {
		if (cellInfo.value === null || cellInfo.value === undefined) {
			return "";
		}
		return "OP" + cellInfo.value + "0";
	}

	viewRow(e: any) {
		this.router.navigate([`${this.itemUrl}/${e.row.data.id}`]);
	}
}